import type { AppointmentTerms } from './appointment.js';
import type { Actor, EventId, LocationId, NpcId, SecretId } from './ids.js';
import type { MemoryTrigger, ObligationSubstance } from './memory.js';
import type { Proposition } from './proposition.js';
import type { StanceSubject } from './npc.js';
import type { RelationshipAxes } from './stats.js';
import type { WorldTime } from './time.js';

/**
 * Conversations produce contracts, not vibes (§14).
 *
 * Every scene exits into a bounded set of machine-readable outcomes. If a
 * scene ends without one, it didn't happen. The type list is closed — eight,
 * resist a ninth — and the payloads are open (§21). Bounds are enforced by
 * the sim, never the model: impossible outcomes are dropped and the prose
 * still stands.
 */

/** The eight. */
export type OutcomeType =
  | 'relationship_shift'
  | 'appointment'
  | 'obligation'
  | 'transfer'
  | 'information'
  | 'secret_exposed'
  | 'memory'
  | 'stance_change';

/** Where a state change came from. Cheap now, invaluable at hour sixty. */
export type OutcomeSource = 'model' | 'sim';

interface OutcomeBase<T extends OutcomeType, P> {
  readonly type: T;
  readonly target: NpcId | LocationId | null;
  readonly payload: P;
  /**
   * The lying flag. An outcome can be true in the sim and false to the
   * player's read layer. He says he'll do it → visible obligation. He has no
   * intention → invisible stance_change. Both fire; the player sees one.
   */
  readonly visible: boolean;
  readonly source: OutcomeSource;
}

// ---------------------------------------------------------------------------
// Payloads. Every shape below is OPEN (§21): the type list is fixed, the
// internals of each payload are not. Each is the smallest thing the §14
// table's one-line description supports.
// ---------------------------------------------------------------------------

/** trust/respect/fear/warmth deltas. Small, capped per scene by the sim. */
// OPEN (§21): the cap, and whether a shift can name a reason.
export type RelationshipShiftPayload = Readonly<Partial<RelationshipAxes>>;

/** place, time, participants, purpose. */
export type AppointmentPayload = AppointmentTerms;

/** Someone owes someone. Direction, substance, deadline or none. */
export interface ObligationPayload {
  readonly from: Actor;
  readonly to: Actor;
  readonly substance: ObligationSubstance;
  readonly deadline: WorldTime | null;
}

/** Money, items, product change hands. Fires immediately. */
export interface TransferPayload {
  readonly from: Actor;
  readonly to: Actor;
  // OPEN (§21, inventory): only money has a settled shape. Items and product
  // are free text until an inventory decision exists.
  readonly what:
    | { readonly kind: 'money'; readonly amount: number }
    | { readonly kind: 'item'; readonly item: string; readonly quantity: number };
}

/**
 * A fact conveyed. Carries a truth value the player never sees: the model
 * is told what he knows and what he's presenting; it emits the claim and
 * flags accuracy.
 */
export interface InformationPayload {
  readonly from: Actor;
  readonly to: Actor;
  /** A triple over real entities (`types/proposition.ts`). Decided: not free text. */
  readonly proposition: Proposition;
  readonly truthful: boolean;
}

/** A secret surfaces. Who now knows. */
export interface SecretExposedPayload {
  readonly secret: SecretId;
  readonly nowKnownBy: readonly Actor[];
}

/** A permanent impression written to an NPC. `target` is the NPC. */
export interface MemoryPayload {
  readonly trigger: MemoryTrigger;
  /** The event being remembered. */
  readonly event: EventId;
}

/** Disposition toward a faction, person, or situation moves. */
export interface StanceChangePayload {
  readonly subject: StanceSubject;
  // OPEN (§21): see `Stance`.
  readonly delta: number;
}

// ---------------------------------------------------------------------------
// The union
// ---------------------------------------------------------------------------

export type RelationshipShiftOutcome = OutcomeBase<'relationship_shift', RelationshipShiftPayload>;
export type AppointmentOutcome = OutcomeBase<'appointment', AppointmentPayload>;
export type ObligationOutcome = OutcomeBase<'obligation', ObligationPayload>;
export type TransferOutcome = OutcomeBase<'transfer', TransferPayload>;
export type InformationOutcome = OutcomeBase<'information', InformationPayload>;
export type SecretExposedOutcome = OutcomeBase<'secret_exposed', SecretExposedPayload>;
export type MemoryOutcome = OutcomeBase<'memory', MemoryPayload>;
export type StanceChangeOutcome = OutcomeBase<'stance_change', StanceChangePayload>;

/** Closed type, open payload. Discriminate on `type`. */
export type Outcome =
  | RelationshipShiftOutcome
  | AppointmentOutcome
  | ObligationOutcome
  | TransferOutcome
  | InformationOutcome
  | SecretExposedOutcome
  | MemoryOutcome
  | StanceChangeOutcome;

/**
 * §14 "small, capped per scene": how much of the per-scene relationship and
 * stance cap has already been spent, on which axis, for which subject.
 * Keyed exactly the way `outcomes.ts`'s `spend()` keys entries —
 * `` `${npc.id}:${axis}` `` for relationships, `` `${npc.id}:${subjectKey}` ``
 * for stances — so a scene's total across every outcome that belongs to it
 * can never exceed the cap no matter how many sim calls the scene is split
 * across.
 *
 * A plain, JSON-serializable record (no `Map`): this rides inside
 * `WorldState`, which must serialize and deserialize byte-identically.
 */
export interface SceneBudget {
  readonly relationship: Readonly<Record<string, number>>;
  readonly stance: Readonly<Record<string, number>>;
}

/** No spend yet — the state a new scene starts from. */
export const EMPTY_SCENE_BUDGET: SceneBudget = { relationship: {}, stance: {} };

/**
 * What every scene returns (§14 Contract Schema). Field names match the
 * spec's schema verbatim, `scene_end` included, because this is the shape the
 * server validates model output against before it reaches the sim (§19).
 */
export interface SceneContract {
  readonly prose: string;
  readonly outcomes: readonly Outcome[];
  readonly scene_end: boolean;
}
